import { DateTimeFormatter, LocalDate } from "@js-joda/core";
import cn from "classnames";
import constate from "constate";
import {
	type ComponentPropsWithRef,
	type FC,
	useEffect,
	useMemo,
	useState,
} from "react";
import { GENERATE_MAX_AHEAD } from "shared";
import { useEventCallback } from "usehooks-ts";
import { ButtonIcon } from "../components/ButtonIcon";
import {
	daysBetween,
	getMonthGridDays,
	getMonthRange,
	getWeekdays,
	isDateInRange,
} from "./calendar.utils";
import { monthYear } from "./date.format";

export type DateRangeValue = [LocalDate, LocalDate];
type DateRangeValuePicker = [LocalDate | undefined, LocalDate | undefined];

interface ContextProps {
	onChange?: (value: DateRangeValue | undefined) => unknown;
}

// Local context
const [Context, useContext] = constate(({ onChange }: ContextProps) => {
	// User for month grids
	const [month, setMonth] = useState(LocalDate.now());
	const [value, setValue] = useState<DateRangeValuePicker>([
		undefined,
		undefined,
	]);

	const [hovered, setHovered] = useState<LocalDate | undefined>(undefined);

	const onClick = useEventCallback((value: LocalDate) =>
		setValue(([from, to]) => {
			if (from && to) {
				return [value, undefined];
			}

			if (!from) {
				return [value, undefined];
			}

			if (from && value.isBefore(from)) {
				return [value, undefined];
			}

			return [from, value];
		}),
	);

	const highlighted = useMemo<LocalDate[]>(() => {
		const [from, to] = value;

		if (from && to) {
			return daysBetween(from, to);
		}

		if (from && hovered) {
			if (hovered.isBefore(from)) {
				return [hovered, from];
			}

			return daysBetween(from, hovered);
		}

		if (from) {
			return [from];
		}

		if (hovered) {
			return [hovered];
		}

		return [];
	}, [hovered, value]);

	const changeMonth = useEventCallback((diff: number) => {
		setMonth((x) => x.plusMonths(diff));
	});

	useEffect(() => {
		const [from, to] = value;
		if (from && to) {
			onChange?.([from, to]);
			return;
		}

		onChange?.(undefined);
	}, [value, onChange]);

	return {
		month,
		changeMonth,
		setHovered,
		highlighted,
		onClick,
	};
});

interface SelectorProps {
	date: LocalDate;
	onPrevMonth?: () => unknown;
	onNextMonth?: () => unknown;
}

const dayNumber = DateTimeFormatter.ofPattern("d");

const Selector: FC<SelectorProps> = (props) => {
	const { date, onPrevMonth, onNextMonth } = props;
	const days = useMemo(() => getMonthGridDays(date), [date]);
	const firstDayOfMonth = date.withDayOfMonth(1);
	const { setHovered, highlighted, onClick } = useContext();
	const now = LocalDate.now();
	const monthRange = getMonthRange(date);
	const maxFutureDate = now.plus(GENERATE_MAX_AHEAD);
	const weekdays = useMemo(() => getWeekdays(), []);

	return (
		<div>
			<div className="date-range-header">
				{onPrevMonth && (
					<ButtonIcon
						aria-label="previous period range"
						icon="chevron_left"
						onClick={() => onPrevMonth()}
					/>
				)}
				<div className="date-range-month">{date.format(monthYear)}</div>
				{onNextMonth && (
					<ButtonIcon
						aria-label="next period range"
						icon="chevron_right"
						onClick={() => onNextMonth()}
					/>
				)}
			</div>
			<div className="date-range-calendar">
				{weekdays.map((weekday) => {
					const name = weekday.name();
					return <span key={`weekday-${name}`}>{name.substring(0, 1)}</span>;
				})}
				{days.map((x) => {
					if (!isDateInRange(x, monthRange)) {
						return <span key={x.toString()} />;
					}
					const disabled =
						x.isBefore(now) ||
						x.isBefore(firstDayOfMonth) ||
						x.isAfter(maxFutureDate);

					return (
						<button
							className={cn("date", {
								active: !disabled && isDateInRange(x, highlighted),
							})}
							disabled={disabled}
							key={x.toString()}
							type="button"
							onClick={() => onClick(x)}
							onMouseEnter={() => setHovered(x)}
							onMouseLeave={() => setHovered(undefined)}
						>
							<span className="circle">{x.format(dayNumber)}</span>
						</button>
					);
				})}
			</div>
		</div>
	);
};

const DateRangeContent: FC<ComponentPropsWithRef<"div">> = (props) => {
	const { className, ...rest } = props;

	const { month, changeMonth } = useContext();

	return (
		<div className={cn("date-range", className)} {...rest}>
			<Selector date={month} onPrevMonth={() => changeMonth(-1)} />
			<Selector date={month.plusMonths(1)} onNextMonth={() => changeMonth(1)} />
		</div>
	);
};

interface DateRangeSelectorProps
	extends Omit<ComponentPropsWithRef<"div">, "onChange"> {
	onChange?: (value: DateRangeValue | undefined) => unknown;
}

export const DateRangeSelector: FC<DateRangeSelectorProps> = (props) => {
	const { onChange, ...rest } = props;
	return (
		<Context onChange={onChange}>
			<DateRangeContent {...rest} />
		</Context>
	);
};
